import { useRef, useEffect } from 'react';
import { personalInfo, skills } from '../../../mocks/portfolio';

const AboutSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="section-padding bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-medium">About Me</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2 text-gray-900 dark:text-white">
              关于我
            </h2>
          </div>
          <p className="text-sm text-gray-400 max-w-xs">用技术语言翻译用户需求，以解决真实用户痛点为核心</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Image */}
          <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-100">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden aspect-[4/5] max-w-sm mx-auto">
                <img
                  src="https://public.readdy.ai/ai/img_res/78be2a2b603cda857d044e9c03aee6a1.jpg"
                  alt={`${personalInfo.name} - AI 产品经理`}
                  title={`${personalInfo.name} - ${personalInfo.title}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Experience badge */}
              <div className="absolute bottom-6 right-0 lg:-right-6 bg-orange-500 text-white rounded-2xl px-5 py-4 shadow-xl">
                <div className="text-3xl font-bold">{personalInfo.experience}</div>
                <div className="text-xs font-medium opacity-80 whitespace-nowrap">产品经验</div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col gap-8">
            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                用技术语言翻译用户需求
              </h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm md:text-base">
                {personalInfo.bio}
              </p>
            </div>

            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-300">
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm md:text-base">
                我相信最好的 AI 产品不是技术驱动，而是以解决真实用户痛点为核心。每一次迭代都应该有数据支撑，每一个功能决策都经过严格的价值评估。
              </p>
            </div>

            {/* Info grid */}
            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-[350ms] grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-map-pin-line', label: '所在地', value: personalInfo.location },
                { icon: 'ri-mail-line', label: '邮箱', value: personalInfo.email },
                { icon: 'ri-briefcase-line', label: '状态', value: '开放合作' },
                { icon: 'ri-building-line', label: '经验', value: `${personalInfo.experience} AI 产品` },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 dark:bg-orange-900/20 flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-orange-500 text-sm`} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{item.label}</div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 break-all">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Skills */}
            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-[400ms]">
              <h4 id="skills" className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                <a href="#skills">核心技能</a>
              </h4>
              <div className="flex flex-wrap gap-2">
                {skills.map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-orange-400 hover:text-orange-500 dark:hover:border-orange-600 dark:hover:text-orange-400 transition-colors cursor-default"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div data-animate className="opacity-0 translate-y-8 transition-all duration-700 delay-[450ms]">
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center gap-2 text-orange-500 font-medium text-sm hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
              >
                查看我的联系方式
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
